/**
 * InviteRollDialog — one click sends a set-password invite to every member who
 * has never set a password. Sends one at a time so the admin watches it happen
 * and can stop half way; anyone already signed in is never bothered.
 */
import React, { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Loader2, Send, Check, MinusCircle, X } from 'lucide-react';

import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';

const authH = () => ({
  'Content-Type': 'application/json',
  Authorization: `Bearer ${localStorage.getItem('eventflow_token') || ''}`,
});

const API = process.env.REACT_APP_BACKEND_URL;

export const InviteRollDialog = ({ churchId, churchName, onClose }) => {
  const qc = useQueryClient();
  const [results, setResults] = useState({});   // member id -> 'sending'|'sent'|'skipped'|'failed'
  const [running, setRunning] = useState(false);
  const [stopped, setStopped] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['member-invite-status', churchId],
    queryFn: async () => {
      const r = await fetch(`${API}/api/church/${churchId}/members/invite-status`, { headers: authH() });
      if (!r.ok) throw new Error('Could not read the roll');
      return r.json();
    },
  });

  const { data: mail } = useQuery({
    queryKey: ['email-status-invite'],
    queryFn: async () => {
      const r = await fetch(`${API}/api/email/status`, { headers: authH() });
      return r.ok ? r.json() : null;
    },
  });

  const pending = data?.pending || [];
  const counts = data?.counts || {};
  const done = Object.values(results).filter((s) => s !== 'sending').length;

  const run = async () => {
    setRunning(true);
    setStopped(false);
    let sent = 0;
    for (const m of pending) {
      if (stopped) break;
      setResults((r) => ({ ...r, [m.id]: 'sending' }));
      try {
        const res = await fetch(`${API}/api/church/${churchId}/members/${m.id}/invite`,
                                { method: 'POST', headers: authH() });
        const body = await res.json();
        if (!res.ok) throw new Error(body?.detail || 'Failed');
        setResults((r) => ({ ...r, [m.id]: body.skipped ? 'skipped' : 'sent' }));
        if (!body.skipped) sent += 1;
      } catch (e) {
        setResults((r) => ({ ...r, [m.id]: 'failed' }));
      }
    }
    setRunning(false);
    qc.invalidateQueries({ queryKey: ['church-members', churchId] });
    qc.invalidateQueries({ queryKey: ['member-invite-status', churchId] });
    toast.success(`${sent} invite${sent === 1 ? '' : 's'} on their way`);
  };

  const icon = (state) => {
    if (state === 'sending') return <Loader2 className="h-3.5 w-3.5 animate-spin text-slate-400" />;
    if (state === 'sent') return <Check className="h-3.5 w-3.5 text-emerald-600" />;
    if (state === 'skipped') return <MinusCircle className="h-3.5 w-3.5 text-slate-400" />;
    if (state === 'failed') return <X className="h-3.5 w-3.5 text-rose-600" />;
    return <span className="h-3.5 w-3.5 rounded-full border border-slate-200 inline-block" />;
  };

  return (
    <Dialog open onOpenChange={(o) => !o && !running && onClose()}>
      <DialogContent className="max-w-lg" data-testid="invite-roll-dialog">
        <DialogHeader>
          <DialogTitle>Invite the roll to the member area</DialogTitle>
          <DialogDescription>
            Everyone at {churchName} who has an email address but has never set a password.
            Members who already sign in are left alone.
          </DialogDescription>
        </DialogHeader>

        {mail && !mail.enabled && (
          <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-3 py-2"
             data-testid="invite-email-off">
            Email is switched off, so invites will wait in the outbox. Add your SMTP details under
            <strong> Settings → Email</strong>, then flush the outbox and they all go out.
          </p>
        )}

        {isLoading ? (
          <div className="py-10 text-center text-sm text-slate-400">
            <Loader2 className="h-4 w-4 animate-spin inline mr-2" />Reading the roll…
          </div>
        ) : (
          <>
            <div className="flex flex-wrap gap-x-5 gap-y-1 text-xs text-slate-500">
              <span data-testid="invite-count-pending"><strong className="text-slate-800">{pending.length}</strong> to invite</span>
              <span data-testid="invite-count-signed-in">{counts.signed_in || 0} already signing in</span>
              <span>{counts.password_set || 0} have a password</span>
              <span data-testid="invite-count-no-email">{counts.no_email || 0} with no email</span>
            </div>

            {(running || done > 0) && (
              <Progress value={pending.length ? (done / pending.length) * 100 : 0}
                        className="h-1.5" data-testid="invite-progress" />
            )}

            <div className="max-h-64 overflow-y-auto divide-y rounded-md border mt-1"
                 data-testid="invite-roll-list">
              {pending.length === 0 && (
                <p className="p-6 text-center text-sm text-slate-400" data-testid="invite-roll-empty">
                  Nobody is waiting — every member with an email can already get in.
                </p>
              )}
              {pending.map((m) => (
                <div key={m.id} className="flex items-center gap-3 px-3 py-2 text-sm"
                     data-testid={`invite-row-${m.id}`}>
                  {icon(results[m.id])}
                  <span className="font-medium text-slate-800 truncate">{m.name || m.email}</span>
                  <span className="text-xs text-slate-400 truncate ml-auto">{m.email}</span>
                  {m.invited_at && !results[m.id] && (
                    <span className="text-[10px] uppercase tracking-wider text-amber-600">invited before</span>
                  )}
                </div>
              ))}
            </div>
          </>
        )}

        <DialogFooter>
          {running ? (
            <Button variant="outline" onClick={() => setStopped(true)} data-testid="invite-stop">
              Stop after this one
            </Button>
          ) : (
            <Button variant="outline" onClick={onClose} data-testid="invite-close">Close</Button>
          )}
          <Button onClick={run} disabled={running || !pending.length}
                  data-testid="invite-roll-send">
            {running ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}
            {running ? `Sending ${done}/${pending.length}` : `Send ${pending.length} invite${pending.length === 1 ? '' : 's'}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default InviteRollDialog;
